import { AnimatePresence, motion } from 'framer-motion';
import { Maximize, Pause, Play, RotateCcw, Volume2, VolumeX, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import axios from '../utils/axiosHandler';

// ModalVideoPlayer component - video player with controls for modal view
const ModalVideoPlayer = ({ item, className }) => {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(true);
    const [isMuted, setIsMuted] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [showControls, setShowControls] = useState(true);
    const controlsTimeoutRef = useRef(null);

    // Auto-hide controls after 3 seconds of inactivity
    const resetControlsTimeout = () => {
        if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current);
        }
        setShowControls(true);
        controlsTimeoutRef.current = setTimeout(() => {
            if (isPlaying) {
                setShowControls(false);
            }
        }, 3000);
    };

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleTimeUpdate = () => {
            setCurrentTime(video.currentTime);
            setProgress((video.currentTime / video.duration) * 100);
        };

        const handleLoadedMetadata = () => {
            setDuration(video.duration);
            video.play().catch(() => { });
        };

        const handleEnded = () => {
            setIsPlaying(false);
        };

        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('ended', handleEnded);

        return () => {
            video.removeEventListener('timeupdate', handleTimeUpdate);
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('ended', handleEnded);
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current);
            }
        };
    }, []);

    const togglePlay = (e) => {
        e.stopPropagation();
        const video = videoRef.current;
        if (!video) return;

        if (isPlaying) {
            video.pause();
        } else {
            video.play();
        }
        setIsPlaying(!isPlaying);
        resetControlsTimeout();
    };

    const toggleMute = (e) => {
        e.stopPropagation();
        const video = videoRef.current;
        if (!video) return;

        video.muted = !isMuted;
        setIsMuted(!isMuted);
        resetControlsTimeout();
    };

    const handleProgressClick = (e) => {
        e.stopPropagation();
        const video = videoRef.current;
        if (!video) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const newProgress = (clickX / rect.width) * 100;
        const newTime = (newProgress / 100) * video.duration;
        video.currentTime = newTime;
        resetControlsTimeout();
    };

    const handleRestart = (e) => {
        e.stopPropagation();
        const video = videoRef.current;
        if (!video) return;

        video.currentTime = 0;
        video.play();
        setIsPlaying(true);
        resetControlsTimeout();
    };

    const handleFullscreen = (e) => {
        e.stopPropagation();
        const video = videoRef.current;
        if (!video) return;

        if (video.requestFullscreen) {
            video.requestFullscreen();
        } else if (video.webkitRequestFullscreen) {
            video.webkitRequestFullscreen();
        }
        resetControlsTimeout();
    };

    const formatTime = (time) => {
        if (isNaN(time)) return '0:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    return (
        <div
            className={`${className} relative group`}
            onMouseMove={resetControlsTimeout}
            onMouseEnter={() => setShowControls(true)}
        >
            <video
                ref={videoRef}
                className={className}
                playsInline
                autoPlay
                preload="auto"
                onClick={togglePlay}
            >
                <source src={item.url} type="video/mp4" />
            </video>

            {/* Video Controls Overlay */}
            <motion.div
                className="absolute inset-0 flex flex-col justify-end pointer-events-none"
                initial={{ opacity: 1 }}
                animate={{ opacity: showControls ? 1 : 0 }}
                transition={{ duration: 0.2 }}
            >
                {/* Center Play/Pause Button */}
                <motion.button
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
                        w-16 h-16 rounded-full bg-black/40 backdrop-blur-sm
                        flex items-center justify-center text-white
                        pointer-events-auto hover:bg-black/60 transition-colors"
                    onClick={togglePlay}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                >
                    {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
                </motion.button>

                {/* Bottom Controls Bar */}
                <div className="bg-gradient-to-t from-black/70 via-black/40 to-transparent p-3 pt-8 pointer-events-auto">
                    {/* Progress Bar */}
                    <div
                        className="w-full h-1.5 bg-white/30 rounded-full cursor-pointer mb-3 group/progress"
                        onClick={handleProgressClick}
                    >
                        <div
                            className="h-full bg-white rounded-full relative transition-all"
                            style={{ width: `${progress}%` }}
                        >
                            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full 
                                opacity-0 group-hover/progress:opacity-100 transition-opacity" />
                        </div>
                    </div>

                    {/* Controls Row */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {/* Play/Pause */}
                            <motion.button
                                className="text-white hover:text-white/80 transition-colors"
                                onClick={togglePlay}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                            >
                                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                            </motion.button>

                            {/* Restart */}
                            <motion.button
                                className="text-white hover:text-white/80 transition-colors"
                                onClick={handleRestart}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                            >
                                <RotateCcw className="w-5 h-5" />
                            </motion.button>

                            {/* Mute/Unmute */}
                            <motion.button
                                className="text-white hover:text-white/80 transition-colors"
                                onClick={toggleMute}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                            >
                                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                            </motion.button>

                            {/* Time Display */}
                            <span className="text-white text-sm font-medium">
                                {formatTime(currentTime)} / {formatTime(duration)}
                            </span>
                        </div>

                        <div className="flex items-center gap-3">
                            {/* Fullscreen */}
                            <motion.button
                                className="text-white hover:text-white/80 transition-colors"
                                onClick={handleFullscreen}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                            >
                                <Maximize className="w-5 h-5" />
                            </motion.button>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

// MediaItem component renders either a video or image based on item.type
const MediaItem = ({ item, className, onClick }) => {
    const videoRef = useRef(null);
    const [isInView, setIsInView] = useState(false);
    const [isBuffering, setIsBuffering] = useState(true);

    // Intersection Observer to detect if video is in view and play/pause accordingly
    useEffect(() => {
        const options = {
            root: null,
            rootMargin: '50px',
            threshold: 0.1
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                setIsInView(entry.isIntersecting);
            });
        }, options);

        if (videoRef.current) {
            observer.observe(videoRef.current);
        }

        return () => {
            if (videoRef.current) {
                observer.unobserve(videoRef.current);
            }
        };
    }, []);

    // Handle video play/pause based on whether the video is in view or not
    useEffect(() => {
        let mounted = true;

        const handleVideoPlay = async () => {
            if (!videoRef.current || !isInView || !mounted) return;

            try {
                if (videoRef.current.readyState >= 3) {
                    setIsBuffering(false);
                    await videoRef.current.play();
                } else {
                    setIsBuffering(true);
                    await new Promise((resolve) => {
                        if (videoRef.current) {
                            videoRef.current.oncanplay = resolve;
                        }
                    });
                    if (mounted) {
                        setIsBuffering(false);
                        await videoRef.current.play();
                    }
                }
            } catch (error) {
                console.warn("Video playback failed:", error);
            }
        };

        if (isInView) {
            handleVideoPlay();
        } else if (videoRef.current) {
            videoRef.current.pause();
        }

        return () => {
            mounted = false;
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.removeAttribute('src');
                videoRef.current.load();
            }
        };
    }, [isInView]);

    if (item.type === 'video') {
        // Check if object-contain is specified in className (for modal view)
        const useContain = className?.includes('object-contain');

        return (
            <div className={`${className} relative`} style={{ overflow: useContain ? 'visible' : 'hidden' }}>
                <video
                    ref={videoRef}
                    className={useContain ? className : "w-full h-full object-cover"}
                    onClick={onClick}
                    playsInline
                    muted
                    loop
                    preload="auto"
                    style={{
                        opacity: isBuffering ? 0.8 : 1,
                        transition: 'opacity 0.2s',
                        transform: 'translateZ(0)',
                        willChange: 'transform',
                    }}
                >
                    <source src={item.url} type="video/mp4" />
                </video>
                {isBuffering && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                        <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    </div>
                )}
            </div>
        );
    }

    return (
        <img
            src={item.url}
            alt={item.title}
            className={`${className} object-cover cursor-pointer`}
            onClick={onClick}
            loading="lazy"
            decoding="async"
        />
    );
};

// GalleryModal component displays the selected media item in a modal
const GalleryModal = ({ selectedItem, isOpen, onClose, setSelectedItem, mediaItems }) => {
    if (!isOpen) return null;

    return (
        <>
            {/* Main Modal */}
            <motion.div
                initial={{ scale: 0.98 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.98 }}
                transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 30
                }}
                className="fixed inset-0 w-full min-h-screen sm:h-[90vh] md:h-[600px] backdrop-blur-lg 
                  rounded-none sm:rounded-lg md:rounded-xl overflow-hidden z-10"
            >
                {/* Main Content */}
                <div className="h-full flex flex-col">
                    <div className="flex-1 p-2 sm:p-3 md:p-4 flex items-center justify-center bg-gray-50/50">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={selectedItem._id}
                                className="relative max-w-[95%] sm:max-w-[85%] md:max-w-3xl 
                                    max-h-[70vh] rounded-lg overflow-visible shadow-md"
                                initial={{ y: 20, scale: 0.97 }}
                                animate={{
                                    y: 0,
                                    scale: 1,
                                    transition: {
                                        type: "spring",
                                        stiffness: 500,
                                        damping: 30,
                                        mass: 0.5
                                    }
                                }}
                                exit={{
                                    y: 20,
                                    scale: 0.97,
                                    transition: { duration: 0.15 }
                                }}
                                onClick={(e) => {
                                    // Only close if clicking outside the video controls
                                    if (selectedItem.type !== 'video') {
                                        onClose();
                                    }
                                }}
                            >
                                {/* Media Container with Close Button */}
                                <div className="relative inline-block">
                                    {/* Close Button on Media */}
                                    <motion.button
                                        className="absolute top-3 right-3 z-20 p-2.5 rounded-full 
                                            bg-black/60 text-white hover:bg-black/80 
                                            backdrop-blur-sm shadow-lg"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onClose();
                                        }}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: 0.1 }}
                                    >
                                        <X className="w-5 h-5" />
                                    </motion.button>

                                    {selectedItem.type === 'video' ? (
                                        <ModalVideoPlayer
                                            item={selectedItem}
                                            className="max-w-full max-h-[70vh] object-contain rounded-lg bg-gray-900/20"
                                        />
                                    ) : (
                                        <>
                                            <img
                                                src={selectedItem.url}
                                                alt={selectedItem.title}
                                                className="max-w-full max-h-[70vh] object-contain rounded-lg bg-gray-900/20"
                                                onClick={onClose}
                                            />
                                            <div className="absolute bottom-0 left-0 right-0 p-2 sm:p-3 md:p-4 
                                                bg-gradient-to-t from-black/50 to-transparent rounded-b-lg">
                                                <h3 className="text-white text-base sm:text-lg md:text-xl font-semibold">
                                                    {selectedItem.title}
                                                </h3>
                                                <p className="text-white/80 text-xs sm:text-sm mt-1">
                                                    {selectedItem.description}
                                                </p>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </motion.div>

            {/* Fixed Bottom Dock */}
            <div className="fixed z-50 left-1/2 bottom-4 -translate-x-1/2">
                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    className="relative rounded-xl bg-sky-400/20 backdrop-blur-xl 
                        border border-blue-400/30 shadow-lg
                        max-w-[50vw] sm:max-w-[90vw] md:max-w-[90vw] lg:max-w-[80vw]"
                >
                    <div className="flex items-center -space-x-2 px-4 py-3 overflow-x-auto scrollbar-thin scrollbar-thumb-white/30 scrollbar-track-transparent"
                        style={{ scrollbarWidth: 'thin' }}
                    >
                        {mediaItems.map((item, index) => (
                            <motion.div
                                key={item._id}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedItem(item);
                                }}
                                style={{
                                    zIndex: selectedItem._id === item._id ? 30 : mediaItems.length - index,
                                }}
                                className={`
                  relative group
                  w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 flex-shrink-0 
                  rounded-lg overflow-hidden 
                  cursor-pointer hover:z-20
                  ${selectedItem._id === item._id
                                        ? 'ring-2 ring-white/70 shadow-lg'
                                        : 'hover:ring-2 hover:ring-white/30'}
                `}
                                initial={{ rotate: index % 2 === 0 ? -15 : 15 }}
                                animate={{
                                    scale: selectedItem._id === item._id ? 1.2 : 1,
                                    rotate: selectedItem._id === item._id ? 0 : index % 2 === 0 ? -15 : 15,
                                    y: selectedItem._id === item._id ? -8 : 0,
                                }}
                                whileHover={{
                                    scale: 1.3,
                                    rotate: 0,
                                    y: -10,
                                    transition: { type: "spring", stiffness: 400, damping: 25 }
                                }}
                            >
                                <MediaItem item={item} className="w-full h-full" onClick={() => setSelectedItem(item)} />
                                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/5 to-white/20" />
                                {selectedItem._id === item._id && (
                                    <motion.div
                                        layoutId="activeGlow"
                                        className="absolute -inset-2 bg-white/20 blur-xl"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ duration: 0.2 }}
                                    />
                                )}
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </>
    );
};

const ITEMS_PER_PAGE = 12;

const Gallery = () => {
    const [selectedItem, setSelectedItem] = useState(null);
    const [items, setItems] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
    const [loadingMore, setLoadingMore] = useState(false);

    useEffect(() => {
        fetchGalleryItems();
    }, []);

    const fetchGalleryItems = async () => {
        try {
            const response = await axios.get('/api/gallery/items');
            setItems(response.data);
        } catch (error) {
            console.error('Error fetching gallery items:', error);
            setError('Failed to load gallery items');
        } finally {
            setLoading(false);
        }
    };

    // Get visible items based on current visibleCount
    const visibleItems = items.slice(0, visibleCount);
    const hasMoreItems = visibleCount < items.length;
    const remainingItems = items.length - visibleCount;

    // Load more items with a smooth animation
    const handleLoadMore = () => {
        setLoadingMore(true);
        // Small delay for smooth transition
        setTimeout(() => {
            setVisibleCount(prev => Math.min(prev + ITEMS_PER_PAGE, items.length));
            setLoadingMore(false);
        }, 300);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-500 text-xl">{error}</p>
                </div>
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <p className="text-gray-500 text-xl">No gallery items available yet.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8 sm:py-12 max-w-9xl">
            <div className="mb-8 sm:mb-12 text-center">
                <motion.h1
                    className="text-3xl sm:text-4xl md:text-5xl font-bold bg-clip-text text-transparent 
                   bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900
                   dark:from-white dark:via-gray-200 dark:to-white"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    Gallery
                </motion.h1>
                <motion.p
                    className="mt-3 text-base sm:text-lg text-gray-600 dark:text-gray-400"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                >
                    Explore our curated collection
                </motion.p>
            </div>

            <AnimatePresence mode="wait">
                {selectedItem ? (
                    <GalleryModal
                        selectedItem={selectedItem}
                        isOpen={true}
                        onClose={() => setSelectedItem(null)}
                        setSelectedItem={setSelectedItem}
                        mediaItems={items}
                    />
                ) : (
                    <>
                        <motion.div
                            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
                            initial="hidden"
                            animate="visible"
                            exit="hidden"
                            variants={{
                                hidden: { opacity: 0 },
                                visible: {
                                    opacity: 1,
                                    transition: { staggerChildren: 0.05 }
                                }
                            }}
                        >
                            {visibleItems.map((item, index) => (
                                <motion.div
                                    key={item._id}
                                    layoutId={`media-${item._id}`}
                                    className="relative aspect-square w-full sm:h-[15rem] lg:h-[20rem] overflow-hidden rounded-xl cursor-pointer shadow-lg"
                                    onClick={() => !isDragging && setSelectedItem(item)}
                                    variants={{
                                        hidden: { y: 50, scale: 0.9, opacity: 0 },
                                        visible: {
                                            y: 0,
                                            scale: 1,
                                            opacity: 1,
                                            transition: {
                                                type: "spring",
                                                stiffness: 350,
                                                damping: 25,
                                                delay: (index % ITEMS_PER_PAGE) * 0.05
                                            }
                                        }
                                    }}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <MediaItem
                                        item={item}
                                        className="absolute inset-0 w-full h-full"
                                        onClick={() => !isDragging && setSelectedItem(item)}
                                    />
                                    <motion.div
                                        className="absolute inset-0 flex flex-col justify-end p-3 sm:p-4"
                                        initial={{ opacity: 0 }}
                                        whileHover={{ opacity: 1 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                                        <h3 className="relative text-white text-sm sm:text-base font-semibold line-clamp-1 drop-shadow-lg">
                                            {item.title}
                                        </h3>
                                        <p className="relative text-white/90 text-xs sm:text-sm mt-1 line-clamp-2 drop-shadow-md">
                                            {item.description}
                                        </p>
                                    </motion.div>
                                </motion.div>
                            ))}
                        </motion.div>

                        {/* Load More Button */}
                        {hasMoreItems && (
                            <motion.div
                                className="flex flex-col items-center mt-10 sm:mt-12"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                            >
                                <p className="text-gray-500 text-sm mb-4">
                                    Showing {visibleCount} of {items.length} items
                                </p>
                                <motion.button
                                    onClick={handleLoadMore}
                                    disabled={loadingMore}
                                    className="px-8 py-3 bg-gradient-to-r from-[#4014b8] to-[#35148f] 
                                        hover:from-[#4014b8] hover:to-[#35148f]
                                        text-white font-medium rounded-full shadow-lg
                                        disabled:opacity-70 disabled:cursor-not-allowed
                                        transition-all duration-300 ease-out
                                        flex items-center gap-2"
                                    whileHover={{ scale: 1.05, boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    {loadingMore ? (
                                        <>
                                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            Loading...
                                        </>
                                    ) : (
                                        <>
                                            Load More
                                            <span className="text-white/70 text-sm">
                                                ({remainingItems} remaining)
                                            </span>
                                        </>
                                    )}
                                </motion.button>
                            </motion.div>
                        )}

                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Gallery;

